import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Eyebrow, fadeUp, scrollToId } from "@/components/landing/shared";
import { useSiteContent } from "@/context/ContentContext";

export default function DiagnosticSection() {
  const { diagnostic, images } = useSiteContent();
  const questions = diagnostic.questions || [];
  return (
    <section
      id="diagnosi"
      className="relative scroll-mt-20 overflow-hidden border-t border-black/[0.08] py-16 md:py-24 lg:py-28"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-[240px]"
        style={{
          background:
            "radial-gradient(closest-side, rgba(16,185,129,0.10), transparent 72%)",
        }}
      />
      <div className="relative mx-auto w-full max-w-6xl px-5 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-12">
          <div className="lg:col-span-7">
            <motion.div
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: "-80px" }}
              variants={fadeUp}
              className="max-w-3xl"
            >
              <Eyebrow>{diagnostic.eyebrow}</Eyebrow>
              <h2 className="mt-4 text-2xl font-semibold leading-tight tracking-[-0.03em] text-[color:var(--text-primary)] sm:text-3xl lg:text-4xl">
                {diagnostic.title}
              </h2>
            </motion.div>

            <div className="mt-10 grid grid-cols-1 gap-3 sm:grid-cols-2">
              {questions.map((q, i) => (
                <motion.div
                  key={`${q}-${i}`}
                  initial="hidden"
                  whileInView="show"
                  viewport={{ once: true, margin: "-30px" }}
                  variants={fadeUp}
                  custom={i}
                  className="flex items-start gap-4 rounded-xl border border-black/10 bg-white px-5 py-4"
                >
                  <span className="mt-0.5 font-mono-tech text-xs text-emerald-700/80 tabnum">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="text-[15px] leading-relaxed text-[color:var(--text-primary)]">
                    {q}
                  </span>
                </motion.div>
              ))}
            </div>
          </div>

          {images?.diagnostic ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, ease: [0.2, 0.8, 0.2, 1] }}
              className="lg:col-span-5"
            >
              <figure className="sticky top-24 overflow-hidden rounded-2xl border border-black/10">
                <img
                  src={images.diagnostic}
                  alt="Analisi di processi e dati aziendali"
                  loading="lazy"
                  className="h-full max-h-[440px] w-full object-cover"
                />
                <figcaption className="border-t border-black/[0.08] bg-white px-4 py-3 text-xs text-tertiary">
                  Ogni progetto parte dall'analisi del processo reale.
                </figcaption>
              </figure>
            </motion.div>
          ) : null}
        </div>

        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-40px" }}
          variants={fadeUp}
          className="mt-14 flex flex-col items-start gap-6"
        >
          <p className="font-display text-3xl font-semibold tracking-[-0.03em] text-[color:var(--text-primary)] sm:text-4xl lg:text-5xl">
            {diagnostic.closing}
          </p>
          <Button
            data-testid="diagnostic-cta-button"
            onClick={() => scrollToId("#contatto")}
            className="h-12 rounded-xl bg-emerald-700 px-6 text-[15px] font-medium text-white transition-colors duration-200 hover:bg-emerald-600"
          >
            {diagnostic.cta}
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </motion.div>
      </div>
    </section>
  );
}
